<template>
  <v-container fluid>
    <v-row align="center" justify="center">
      <v-col cols="12" lg="8">
        <v-card>
          <v-card-title>
            <v-icon large class="mr-2">mdi-account-multiple</v-icon>
            <h3>{{ $tt('customConnectionTitle') }}</h3>
          </v-card-title>
          <v-card-text>
            <v-progress-linear v-if="loading" indeterminate class="mb-2" />

            <!-- メールアドレス指定のフォローリクエスト -->
            <v-form @submit.prevent="followByEmail()">
              <v-row dense align="center">
                <v-col cols="12" sm="8">
                  <v-text-field
                    v-model="followEmail"
                    type="email"
                    dense
                    hide-details
                    prepend-inner-icon="mdi-email-outline"
                    :label="$tt('feed.followByEmail')"
                  />
                </v-col>
                <v-col cols="12" sm="4">
                  <v-btn small color="primary" type="submit" :loading="followEmailSending" :disabled="!followEmail">
                    {{ $tt('customFollowRequest') }}
                  </v-btn>
                </v-col>
              </v-row>
            </v-form>
            <v-divider class="my-3" />

            <!-- 受信リクエスト -->
            <template v-if="incoming.length">
              <h4 class="mb-2">{{ $tt('customFollowRequest') }}</h4>
              <div v-for="r in incoming" :key="'in-' + r.username" class="d-flex align-center mb-2">
                <span>{{ r.nickname || r.username }}</span>
                <v-spacer />
                <v-btn small color="primary" class="mr-2" @click="accept(r.username)">
                  {{ $tt('feed.followAccept') }}
                </v-btn>
                <v-btn small text @click="decline(r.username)">{{ $tt('feed.followDecline') }}</v-btn>
              </div>
              <v-divider class="my-3" />
            </template>

            <v-tabs v-model="tab" color="primary">
              <v-tab class="text-none">{{ $tt('customFollowing') }} ({{ following.length }})</v-tab>
              <v-tab class="text-none">{{ $tt('customFollower') }} ({{ followers.length }})</v-tab>
            </v-tabs>
            <v-tabs-items v-model="tab">
              <v-tab-item>
                <v-card-text v-if="following.length === 0 && outgoing.length === 0">
                  {{ $tt('customHaveNoFollowings') }}
                </v-card-text>
                <v-list v-else dense>
                  <v-list-item v-for="f in following" :key="'fw-' + f.username">
                    <v-list-item-avatar><v-icon>mdi-account</v-icon></v-list-item-avatar>
                    <v-list-item-content>{{ f.nickname || f.username }}</v-list-item-content>
                    <v-list-item-action>
                      <v-btn small text color="warning" @click="unfollowTarget = f">
                        {{ $tt('feed.unfollow') }}
                      </v-btn>
                    </v-list-item-action>
                  </v-list-item>
                  <v-list-item v-for="f in outgoing" :key="'og-' + f.username">
                    <v-list-item-avatar><v-icon>mdi-account-clock</v-icon></v-list-item-avatar>
                    <v-list-item-content>
                      {{ f.nickname || f.username }}
                      <span class="caption grey--text ml-2">{{ $tt('feed.followRequested') }}</span>
                    </v-list-item-content>
                    <v-list-item-action>
                      <v-btn small text @click="unfollow(f.username)">{{ $tt('customCancel') }}</v-btn>
                    </v-list-item-action>
                  </v-list-item>
                </v-list>
              </v-tab-item>
              <v-tab-item>
                <v-card-text v-if="followers.length === 0">{{ $tt('customHaveNoFollowers') }}</v-card-text>
                <v-list v-else dense>
                  <v-list-item v-for="f in followers" :key="'fl-' + f.username">
                    <v-list-item-avatar><v-icon>mdi-account</v-icon></v-list-item-avatar>
                    <v-list-item-content>{{ f.nickname || f.username }}</v-list-item-content>
                  </v-list-item>
                </v-list>
              </v-tab-item>
            </v-tabs-items>
            <p class="caption grey--text mt-3">{{ $tt('feed.connectionsHint') }}</p>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-dialog :value="!!unfollowTarget" max-width="360" @input="unfollowTarget = null">
      <v-card>
        <v-card-text class="pt-4">{{ $tt('feed.unfollowConfirm') }}</v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="unfollowTarget = null">{{ $tt('customCancel') }}</v-btn>
          <v-btn color="warning" text @click="unfollow(unfollowTarget.username)">
            {{ $tt('feed.unfollow') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import UserInfo from '@/lib/user-info'
import feedApiClient from '@/lib/feed-api-client'
import connectManagerClient from '@/lib/connect-client/connect-manager-client'
import Toast from '@/lib/toast'

export default {
  name: 'FeedConnections',
  data() {
    return {
      loading: false,
      tab: 0,
      following: [],
      followers: [],
      incoming: [],
      outgoing: [],
      unfollowTarget: null,
      followEmail: '',
      followEmailSending: false,
    }
  },
  mounted() {
    this.load()
  },
  methods: {
    async load() {
      this.loading = true
      try {
        const res = await feedApiClient.follows()
        this.following = res.data.following || []
        this.followers = res.data.followers || []
        this.incoming = res.data.incoming || []
        this.outgoing = res.data.outgoing || []
      } catch (e) {
        console.error(e.response || e)
        Toast.i18nError('feed.loadError')
      } finally {
        this.loading = false
      }
    },
    // メールアドレスからユーザーを解決してフォローリクエストを送る
    async followByEmail() {
      const email = (this.followEmail || '').trim()
      if (!email) return
      this.followEmailSending = true
      try {
        const res = await connectManagerClient.searchUserByEmail(email)
        const user = res.data
        if (!user || !user.username) {
          Toast.i18nError('feed.emailUserNotFound')
          return
        }
        if (user.username === UserInfo.getUsername()) {
          Toast.i18nError('feed.emailUserNotFound')
          return
        }
        await feedApiClient.requestFollow(user.username, UserInfo.getNickname())
        Toast.i18nSuccess('feed.followRequestSent')
        this.followEmail = ''
        this.load()
      } catch (e) {
        console.error(e.response || e)
        if (e.response && e.response.status === 404) {
          Toast.i18nError('feed.emailUserNotFound')
        } else {
          Toast.i18nError('feed.shareFailed')
        }
      } finally {
        this.followEmailSending = false
      }
    },
    async accept(username) {
      try {
        await feedApiClient.acceptFollow(username, UserInfo.getNickname())
        Toast.i18nSuccess('feed.followAccepted')
        this.load()
      } catch (e) {
        console.error(e.response || e)
        Toast.i18nError('feed.shareFailed')
      }
    },
    async decline(username) {
      try {
        await feedApiClient.declineFollow(username)
        this.load()
      } catch (e) {
        console.error(e.response || e)
        Toast.i18nError('feed.shareFailed')
      }
    },
    async unfollow(username) {
      try {
        await feedApiClient.unfollow(username)
        this.unfollowTarget = null
        this.load()
      } catch (e) {
        console.error(e.response || e)
        Toast.i18nError('feed.shareFailed')
      }
    },
  },
}
</script>
